<template>
  <div v-if="visible" class="imp-banner" role="status">
    <EyeIcon class="imp-banner__icon" :size="16" aria-hidden="true" />
    <span class="imp-banner__who">
      <span class="imp-banner__lead">{{ t('impersonation.banner.viewingAs') }}</span>
      <strong class="imp-banner__name">{{ name }}</strong>
      <span v-if="roleLabel" class="imp-banner__role"> · {{ roleLabel }}</span>
    </span>
    <span class="imp-banner__meta">
      <span class="imp-banner__mode">{{ t('impersonation.banner.readOnly') }} · </span>
      {{ t('impersonation.banner.remaining', { minutes }) }}
    </span>
    <AppButton size="small" variant="secondary" @click="auth.endImpersonation()">
      {{ t('impersonation.banner.end') }}
    </AppButton>
  </div>
</template>

<script setup lang="ts">
/**
 * Плашка вкладки просмотра «глазами агента» (DEV-2400, ADR-0064). Та же, что в
 * CRM: стоит в App.vue над RouterView, высоту отдаёт переменной
 * --impersonation-banner-height, на неё сдвигаются сайдбар и мобильная шапка.
 * Макет — Figma «CRM — Redesign», полоса 7, фрейм 7.7.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { EyeIcon } from 'lucide-vue-next'
import { AppButton } from '@/design-system'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import { getPrimaryRole } from '@/features/auth/types/auth.types'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const session = auth.impersonation

// Атрибут ставится сразу, до отрисовки дашборда: иначе шапка на кадр
// вставала бы под плашку и прыгала.
if (session) document.documentElement.dataset.impersonating = 'true'

// На экранах входа и завершения просмотра плашке нечего показывать.
const visible = computed(() => !!session && !route.meta.skipAuthInit)

const name = computed(() => auth.user?.name || auth.user?.email || '')

const roleLabel = computed(() => {
  const key = getPrimaryRole(auth.user?.roles)
  return key ? t(`auth.roles.${key}`) : ''
})

const now = ref(Date.now())
let timer: ReturnType<typeof setInterval> | null = null

const minutes = computed(() =>
  session ? Math.max(0, Math.ceil((Date.parse(session.expiresAt) - now.value) / 60_000)) : 0,
)

// Сеанс истёк, пока вкладка открыта: сервер ответил бы 401 на первом же
// запросе, но ждать его незачем. Вкладку, открытую уже после конца сеанса,
// уводит гард роутера.
watch(minutes, (left) => {
  if (session && left <= 0 && !route.meta.skipAuthInit)
    router.replace({ name: 'impersonation-ended' })
})

// Вкладки просмотра узнаются по заголовку, пока плашка не видна.
const baseTitle = document.title
watch(
  name,
  (value) => {
    if (session && value) document.title = `👁 ${value} · ${baseTitle}`
  },
  { immediate: true },
)

onMounted(() => {
  if (session) timer = setInterval(() => (now.value = Date.now()), 15_000)
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<style scoped>
.imp-banner {
  position: sticky;
  top: 0;
  /* Выше мобильной шапки (20), ниже выезжающего меню «Ещё» (50). */
  z-index: 30;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm, 0.5rem);
  height: var(--impersonation-banner-height);
  padding: 0 var(--spacing-lg, 1rem);
  border-bottom: 1px solid var(--color-warning, #f59e0b);
  background: var(--color-warning-tint, #fffbeb);
  color: var(--color-warning-strong, #b45309);
  font-size: var(--font-size-sm, 0.8125rem);
}

.imp-banner__icon {
  flex-shrink: 0;
}

.imp-banner__who {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.imp-banner__lead {
  margin-right: 0.25em;
}

.imp-banner__name {
  font-weight: 600;
}

.imp-banner__meta {
  flex-shrink: 0;
  white-space: nowrap;
}

/* Телефон: имя, остаток и кнопка — остальное читается с десктопа. */
@media (max-width: 600px) {
  .imp-banner {
    padding: 0 var(--spacing-sm, 0.5rem);
  }

  .imp-banner__lead,
  .imp-banner__role,
  .imp-banner__mode {
    display: none;
  }

  .imp-banner :deep(.app-button) {
    min-height: 44px;
  }
}
</style>
